import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BucketsLoading from './BucketsLoading';
import { useDrop } from 'react-dnd';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { api } from '../../../app/http-common';
import { PBNEW_STORY, activeSuggestions, chipColor, initials, LS_FAVOURITES } from '../constants';
import { sortClients } from '../hooks/useBucketData';

// Corrections spec 6.9 — "Target buckets".
// Fixed on the right, expanded by default, collapsible to a narrow rail.
// Pins and manual-order arrows are removed; the side/bottom switch is removed.

function TargetBucket({ bucket, active, onDropStory, onOpen, onPreview, acceptTypes = PBNEW_STORY }) {
  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: acceptTypes,
      drop: (dragged) => onDropStory(dragged, bucket),
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [bucket._id, onDropStory, acceptTypes],
  );

  return (
    <div
      ref={dropRef}
      className={`pbnew-folder ${active ? 'active' : ''} ${isOver ? 'drag-over' : ''}`}
      onClick={() => onOpen(bucket)}
      title={bucket.name}
    >
      <FolderOutlinedIcon style={{ fontSize: 16 }} />
      {/* Spec 6.9 — complete names, wrapping onto multiple lines. */}
      <span className="pbnew-folder-name" title={bucket.name}>{bucket.name}</span>
      <span className="pbnew-folder-count">{activeSuggestions(bucket)}</span>
      {/* Follow-up §11 — the same eye-icon preview the overflow and bulk
          destination selectors already offer. Read-only, opens in place, and
          never navigates away from Bucket Stories. */}
      <button
        type="button"
        className="pbnew-folder-preview"
        onClick={(e) => { e.stopPropagation(); onPreview(bucket); }}
        title={`Preview ${bucket.name}`}
        aria-label={`Preview ${bucket.name}`}
      >
        <VisibilityOutlinedIcon style={{ fontSize: 14 }} />
      </button>
    </div>
  );
}

function ClientGroup({ client, expanded, activeBucketId, onToggle, onDropStory, onOpen, onPreview, acceptTypes = PBNEW_STORY, dropHint }) {
  const { clientId, clientName, buckets, metrics } = client;
  const [quickDestination, setQuickDestination] = useState(buckets[0]?._id || '');
  const openTimer = useRef(null);

  // Hovering a collapsed publisher while dragging opens it after 420ms.
  const [, dropRef] = useDrop(
    () => ({
      accept: acceptTypes,
      hover: () => {
        if (expanded || openTimer.current) return;
        openTimer.current = setTimeout(() => {
          onToggle(clientId, true);
          openTimer.current = null;
        }, 420);
      },
      collect: () => ({}),
    }),
    [expanded, clientId, onToggle, acceptTypes],
  );
  useEffect(() => () => { if (openTimer.current) clearTimeout(openTimer.current); }, []);

  return (
    <div className="pbnew-client">
      <div ref={dropRef} className="pbnew-client-row" onClick={() => onToggle(clientId)}>
        <span className={`pbnew-chev ${expanded ? 'open' : ''}`}>
          <KeyboardArrowRightOutlinedIcon style={{ fontSize: 18 }} />
        </span>
        <span className="pbnew-chip" style={{ background: chipColor(clientName) }}>{initials(clientName)}</span>
        <span className="pbnew-client-name" title={clientName}>{clientName}</span>
        <div className="pbnew-client-right" onClick={(e) => e.stopPropagation()}>
          {/* Spec 6.9 — the dropdown picks a QUICK DESTINATION only. It must not
              change the source bucket, so it never navigates. */}
          {buckets.length > 1 && (
            <select
              className="pbnew-mini-select"
              value={quickDestination}
              onChange={(e) => setQuickDestination(e.target.value)}
              title="Quick destination"
            >
              {buckets.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
          )}
          <span className="pbnew-client-count">{metrics.activeSuggestions}</span>
        </div>
      </div>

      {expanded && (
        <>
          <div className="pbnew-folders">
            {buckets.map((b) => (
              <TargetBucket
                key={b._id}
                bucket={b}
                active={b._id === activeBucketId}
                onDropStory={onDropStory}
                onOpen={onOpen}
                onPreview={onPreview}
                acceptTypes={acceptTypes}
              />
            ))}
          </div>
          <div className="pbnew-drop-hint">{dropHint}</div>
        </>
      )}
    </div>
  );
}

export default function TargetBucketsPanel({
  clients,
  activeBucketId,
  collapsed,
  onToggleCollapsed,
  onDropStory,
  onOpenBucket,
  expandedIds,
  onToggleClient,
  onRestoreExpanded,
  loading = false,
  // Adi #1 — the panel is also mounted on the main Stories screen, where the
  // dragged item is a feed story rather than one already in a bucket. The host
  // decides what a drop MEANS; the panel only needs to know which drag types
  // to light up for.
  acceptTypes = PBNEW_STORY,
  dropHint = 'Drop a story onto a bucket to move or copy it.',
  title = 'Target buckets',
}) {
  const [search, setSearch] = useState('');
  // §11 — read-only destination preview, rendered in place inside the panel.
  const [preview, setPreview] = useState(null); // { bucket, items, loading, error }
  // §10 — the expansion state from BEFORE a search auto-revealed matches, so
  // clearing the search restores it instead of leaving every hit open.
  const preSearchExpandedRef = useRef(null);

  // §9 — the panel must order publishers exactly like Publisher Buckets and the
  // two working destination selectors: favourites first, then the sort chosen on
  // Publisher Buckets. Read from the same localStorage keys rather than lifting
  // state, because these screens do not share a store (MoveToModal does the
  // same thing for the same reason).
  const orderedClients = useMemo(() => {
    let sortMode = 'recent';
    let favourites = [];
    try {
      sortMode = window.localStorage.getItem('alchemiq_pbnew_sort') || 'recent';
      const raw = window.localStorage.getItem(LS_FAVOURITES);
      if (raw) favourites = JSON.parse(raw) || [];
    } catch { /* ignore — quota or private-mode */ }
    const sorted = sortClients(clients, sortMode);
    const favSet = new Set(favourites.map(String));
    const favs = sorted.filter((c) => favSet.has(String(c.clientId)));
    const rest = sorted.filter((c) => !favSet.has(String(c.clientId)));
    return [...favs, ...rest];
  }, [clients]);

  const openPreview = useCallback(async (bucket) => {
    setPreview({ bucket, items: null, loading: true, error: null });
    try {
      const res = await api.getBucketStories(bucket._id, {
        status: 'suggestion', limit: 30, include_archived: 'hide',
      });
      setPreview({ bucket, items: res.items || [], loading: false, error: null });
    } catch (err) {
      setPreview({
        bucket, items: null, loading: false,
        error: err?.response?.data?.error || 'Could not load this bucket.',
      });
    }
  }, []);

  // §10 — when the search matches a BUCKET name, reveal it under its publisher
  // straight away. Previously the publisher came back collapsed and the match
  // needed a second click to reach. Only bucket-name hits auto-expand; a
  // publisher-name hit keeps the existing behaviour.
  useEffect(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      if (preSearchExpandedRef.current) {
        const restore = preSearchExpandedRef.current;
        preSearchExpandedRef.current = null;
        onRestoreExpanded?.(restore);
      }
      return;
    }
    if (preSearchExpandedRef.current == null) {
      preSearchExpandedRef.current = [...expandedIds];
    }
    for (const c of clients) {
      if (c.clientName.toLowerCase().includes(query)) continue;
      if (c.buckets.some((b) => (b.name || '').toLowerCase().includes(query))) {
        if (!expandedIds.has(c.clientId)) onToggleClient(c.clientId, true);
      }
    }
    // `expandedIds` is deliberately omitted: the effect is search-driven, and
    // re-running on every manual toggle would fight the user's own clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, clients]);

  if (collapsed) {
    return (
      <aside className="pbnew-targets collapsed">
        <button className="pbnew-icon-btn" onClick={onToggleCollapsed} title="Show target buckets">
          <ChevronRightOutlinedIcon style={{ fontSize: 18, transform: 'rotate(180deg)' }} />
        </button>
        <div className="pbnew-targets-rail-label">{title}</div>
      </aside>
    );
  }

  const q = search.trim().toLowerCase();
  const list = q
    ? orderedClients
        .map((c) => (c.clientName.toLowerCase().includes(q)
          ? c
          : { ...c, buckets: c.buckets.filter((b) => (b.name || '').toLowerCase().includes(q)) }))
        .filter((c) => c.buckets.length > 0)
    : orderedClients;

  return (
    <aside className="pbnew-targets">
      <div className="pbnew-targets-head">
        <span className="pbnew-targets-title">
          <FolderOutlinedIcon style={{ fontSize: 16 }} /> {title}
        </span>
        <button className="pbnew-icon-btn" onClick={onToggleCollapsed} title="Collapse">
          <ChevronRightOutlinedIcon style={{ fontSize: 18 }} />
        </button>
      </div>

      {/* Spec 6.9 — search stays reachable without scrolling the destinations. */}
      <div className="pbnew-targets-search">
        <div className="pbnew-search" style={{ maxWidth: 'none' }}>
          <SearchOutlinedIcon style={{ fontSize: 16 }} />
          <input placeholder="Find publisher or bucket" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="pbnew-targets-scroll">
        {/* §11 — the preview replaces the destination list IN PLACE. Search
            text, expanded groups, the selected destination, the checked source
            stories and the Bucket Stories scroll position are all owned
            elsewhere and untouched by opening or closing it. */}
        {preview ? (
          <div className="pbnew-targets-preview">
            <button className="pbnew-btn" onClick={() => setPreview(null)} style={{ width: '100%' }}>
              <ArrowBackOutlinedIcon style={{ fontSize: 15 }} /> Back to destinations
            </button>
            <div className="pbnew-targets-preview-head" title={preview.bucket.name}>
              {preview.bucket.name}
            </div>
            {preview.loading && <div className="pbnew-drop-hint">Loading…</div>}
            {preview.error && <div className="pbnew-drop-hint error">{preview.error}</div>}
            {preview.items && preview.items.length === 0 && (
              <div className="pbnew-drop-hint">No active suggestions in this bucket.</div>
            )}
            {preview.items && preview.items.length > 0 && (
              <ul className="pbnew-targets-preview-list">
                {preview.items.map((it) => (
                  <li key={it._id}>
                    {it.story?.title || `Story ${String(it.storyId).slice(-6)}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <>
            {list.map((c) => (
              <ClientGroup
                key={c.clientId}
                client={c}
                expanded={expandedIds.has(c.clientId)}
                activeBucketId={activeBucketId}
                onToggle={onToggleClient}
                onDropStory={onDropStory}
                onOpen={onOpenBucket}
                onPreview={openPreview}
                acceptTypes={acceptTypes}
                dropHint={dropHint}
              />
            ))}
            {/* A loader, never an empty state, while the bucket list is still
                in flight — "No matching destinations" during a load reads as a
                real (and alarming) answer. */}
            {loading && <BucketsLoading size={110} />}
            {!loading && list.length === 0 && (
              <div className="pbnew-drop-hint">No matching destinations.</div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
